import { SolanaTransactionInfo } from "@glow/shared";
import { DateTime, Duration } from "luxon";
import React from "react";
import { InformationRow } from "../layout/InformationRow";
import { Markdown } from "../Markdown";
import { TransactionCategory } from "./TransactionCategory";
import { TransactionCategoryIcon } from "./TransactionCategoryIcon";
import { TransactionIcon } from "./TransactionIcon";
import { TransactionSectionTitle } from "./TransactionSectionTitle";
import { TransactionValue } from "./TransactionValue";

export const TransactionActivityRow = ({
  transactionInfo,
  prevTransactionInfo,
  stickyHeader,
  sectionClassName,
  categoryClassName,
  mode = "normal",
  rowClassName,
  highlight,
  onClick,
}: {
  transactionInfo: SolanaTransactionInfo.TransactionInfo;
  prevTransactionInfo?: SolanaTransactionInfo.TransactionInfo;
  stickyHeader?: boolean;
  sectionClassName?: string;
  categoryClassName?: string;
  mode?: "normal" | "simple" | "nft-collection-sales";
  rowClassName?: string;
  highlight?: boolean;
  onClick?: () => void;
}) => {
  const { value, sub_value } = transactionInfo;

  let showCategory = mode === "normal";
  if (prevTransactionInfo) {
    if (
      prevTransactionInfo.category.title === transactionInfo.category.title &&
      prevTransactionInfo.category.icon === transactionInfo.category.icon
    ) {
      showCategory = false;
    }
  }

  const sectionTitleCurrent = getSectionTitle(transactionInfo.timestamp);
  const sectionTitlePrevious = prevTransactionInfo
    ? getSectionTitle(prevTransactionInfo.timestamp)
    : null;

  let sectionTitle: string | null = null;
  if (mode !== "simple") {
    sectionTitle =
      sectionTitleCurrent !== sectionTitlePrevious ? sectionTitleCurrent : null;
  }

  if (sectionTitle && mode === "normal") {
    showCategory = true;
  }

  return (
    <>
      {sectionTitle && (
        <TransactionSectionTitle
          title={sectionTitle}
          sticky={stickyHeader}
          className={sectionClassName}
        />
      )}

      {showCategory && (
        <TransactionCategory
          className={categoryClassName}
          timestamp={transactionInfo.timestamp}
          title={transactionInfo.category.title}
          icon={transactionInfo.category.icon}
        />
      )}

      <InformationRow
        className={rowClassName}
        icon={
          mode === "simple" ? (
            <TransactionCategoryIcon
              className={highlight ? "text-orange" : undefined}
              type={transactionInfo.category.icon}
              display="simple"
            />
          ) : (
            <TransactionIcon icon={transactionInfo.icon} />
          )
        }
        title={
          <Markdown className={highlight ? "text-orange" : undefined}>
            {transactionInfo.title}
          </Markdown>
        }
        subtitle={<Markdown>{transactionInfo.subtitle}</Markdown>}
        value={
          value && (
            <TransactionValue
              {...value}
              highlight={highlight}
              sol_price_usd={transactionInfo.sol_exchange_rate.price_usd}
            />
          )
        }
        subValue={
          sub_value && (
            <div className="text-tertiary fs-sm text-right">{sub_value}</div>
          )
        }
        onClick={onClick}
      />
    </>
  );
};

export const getSectionTitle = (timestamp: string): string | null => {
  const ts = DateTime.fromISO(timestamp);
  const now = DateTime.now();
  const timeDiff = now.diff(ts);

  if (ts.hasSame(now, "day")) {
    return "Today"; // TODO: internationalize this
  } else if (ts.hasSame(now.minus({ day: 1 }), "day")) {
    return "Yesterday";
  } else if (timeDiff < Duration.fromObject({ days: 6 })) {
    // We do 6 days here rather than 7 days since if the tx is 1 week ago, you don't want to show
    // the day of the week (which is the same as the current day of the week)
    return "Last Week";
  } else if (ts.hasSame(now, "year")) {
    return ts.toLocaleString({ month: "long" });
  } else {
    return ts.toLocaleString({ year: "numeric", month: "long" });
  }
};
